/* Settings — Account, Credits, Language and region, Help (print history, support, tour), Your computers, Canva accounts.
 * StudioCore desktop parity (m11 #8): its account modal + gear menu + SupportModal. */
import { useEffect, useState } from 'react';
import { TopBar } from '../app/AppShell.jsx';
import { initialsFor, useSession } from '../app/session.jsx';
import { useWallet } from '../app/wallet.jsx';
import { api } from '../api/client.js';
import { schoolMe } from '../api/school.js';
import { YourComputers } from '../features/computers/computers.jsx';
import { CanvaAccountPicker, useCanvaAccounts } from '../features/browser/canva.jsx';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Icon, Skeleton, Tag } from '../ui/index.jsx';
import { LanguageRegion, PrintHistoryModal, SignOutButton, SupportModal, methodLabel } from '../features/settings/settings.jsx';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n/index.js';

const day = (iso) => new Date(iso).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' });

function Account() {
  const { t } = useTranslation('bv');
  const { session } = useSession();
  const [school, setSchool] = useState(null);
  useEffect(() => { schoolMe().then((r) => setSchool(r.ok ? r : {})); }, []);
  return (
    <Card className="bv-settings__card" data-testid="account">
      <span className="bv-avatar bv-settings__avatar" aria-hidden="true">{initialsFor(session)}</span>
      <div className="bv-settings__body">
        <h2 className="bv-section-title">{(session && session.name) || t('shell.yourAccount')}</h2>
        <p className="bv-muted">{session && session.email}</p>
        <p className="bv-small bv-muted" data-testid="login-method">{t('settings.signedInWith', { method: methodLabel(session && session.loginMethod) })}{session && session.licenseKey ? ` · ${t('settings.license')} ${session.licenseKey}` : ''}</p>
        <p className="bv-small">{!school ? <Skeleton width={180} height={14} /> : school.schoolName ? <>{school.schoolName}{school.isOrgAdmin ? <> · <Tag color="green">{t('settings.approver')}</Tag></> : school.canPrint === false ? <> · <Tag color="yellow">{t('settings.needsApproval')}</Tag></> : null}</> : t('settings.noSchool')}</p>
      </div>
      <SignOutButton />
    </Card>
  );
}

function Credits() {
  const { t } = useTranslation('bv');
  const { balance, openCredits } = useWallet();
  const [tx, setTx] = useState(null);
  useEffect(() => { api('/wallet/transactions?limit=5').then((r) => setTx(r.ok && r.data && r.data.data ? r.data.data.transactions : [])); }, [balance]);
  return (
    <Card className="bv-settings__card bv-settings__card--col" data-testid="credits">
      <div className="bv-settings__row">
        <span className="bv-icontile bv-icontile--purple"><Icon name="sparkle" size={26} /></span>
        <div className="bv-settings__body">
          <h2 className="bv-section-title">{balance == null ? '…' : t('common.credits', { count: balance })}</h2>
          <p className="bv-small bv-muted">{t('settings.creditsHint')}</p>
        </div>
        <Button variant="purple" icon="sparkle" onClick={openCredits}>{t('settings.getCredits')}</Button>
      </div>
      {tx && tx.length > 0 && (
        <ul className="bv-settings__tx">
          {tx.map((t) => <li key={t.id}><span>{t.description || t.type}</span><span className="bv-muted">{day(t.created_at)}</span><b className={t.amount < 0 ? 'is-neg' : 'is-pos'}>{t.amount > 0 ? '+' : ''}{t.amount}</b></li>)}
        </ul>
      )}
    </Card>
  );
}

function CanvaAccounts() {
  const { t } = useTranslation('bv');
  const { accounts, off, reload } = useCanvaAccounts();
  const navigate = useNavigate();
  return (
    <section id="canva" className="bv-settings__section" data-testid="settings-canva">
      <div className="bv-section-head"><h2 className="bv-section-title">{t('settings.canvaAccounts')}</h2></div>
      {!accounts ? <Skeleton height={120} radius={24} /> : off ? <Card><p className="bv-muted">{t('canva.off.title')}.</p></Card>
        : <CanvaAccountPicker accounts={accounts} returnPath="/settings#canva" onChanged={reload} onOpen={(a) => { try { localStorage.setItem('bv.canva.account', a.id); } catch { /* blocked */ } navigate('/browser'); }} compact />}
    </section>
  );
}

function Help() {
  const { t } = useTranslation('bv');
  const [open, setOpen] = useState(null);
  // ?open=history|support (gear menu / Home footer / Print page links)
  useEffect(() => { const o = new URLSearchParams(location.search).get('open'); if (o === 'history' || o === 'support') setOpen(o); }, []);
  return (
    <Card className="bv-settings__card bv-settings__card--col" data-testid="help">
      <h2 className="bv-section-title">{t('settings.help')}</h2>
      <div className="bv-settings__help">
        <Button icon="print" onClick={() => setOpen('history')} data-testid="open-history">{t('shell.printHistory')}</Button>
        <Button icon="sparkle" onClick={() => window.dispatchEvent(new Event('pse-start-tour'))} data-testid="take-tour">{t('shell.takeTour')}</Button>
        <Button variant="blue" icon="resources" onClick={() => setOpen('support')} data-testid="open-support">{t('settings.contactSupport')}</Button>
      </div>
      <PrintHistoryModal open={open === 'history'} onClose={() => setOpen(null)} />
      <SupportModal open={open === 'support'} onClose={() => setOpen(null)} />
    </Card>
  );
}

export default function SettingsPage() {
  const { t } = useTranslation('bv');
  return (
    <div className="bv-content bv-settings">
      <TopBar title={t('shell.settings')} subtitle={t('settings.subtitle')} />
      <div className="bv-settings__top">
        <Account />
        <Credits />
      </div>
      <div className="bv-settings__top">
        <LanguageRegion />
        <Help />
      </div>
      <YourComputers />
      <CanvaAccounts />
    </div>
  );
}
